import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Upload, Grid, Inbox, LayoutDashboard, BookOpen, User, BarChart2,
         CheckSquare, Bell, Newspaper, Menu, X, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { NetworkBanner } from '@/components/ui/NetworkBanner'
import { useUploadStore } from '@/stores/uploadStore'
import { useQuery } from '@tanstack/react-query'
import { supabase, fetchInboxCount, fetchPlatformConfig } from '@/lib/supabase'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import AuthPage              from '@/pages/AuthPage'
import VenueEntryPage        from '@/pages/VenueEntryPage'
import OnboardingPage        from '@/pages/OnboardingPage'
import DeepLinkPage          from '@/pages/DeepLinkPage'
import NotificationPrefsPage from '@/pages/NotificationPrefsPage'
import UploadPage            from '@/pages/UploadPage'
import FeedPage              from '@/pages/FeedPage'
import InboxPage             from '@/pages/InboxPage'
import CreatorRequestsPage   from '@/pages/CreatorRequestsPage'
import DashboardPage         from '@/pages/DashboardPage'
import OrganiserPage         from '@/pages/OrganiserPage'
import PressPage             from '@/pages/PressPage'
import BuyerBriefPage        from '@/pages/BuyerBriefPage'
import PortfolioPage         from '@/pages/PortfolioPage'
import { VenueApp }          from './venue/VenueApp'
import { ApprovalTokenPage } from '@/pages/approve/ApprovalTokenPage'
import { OneDriveCallback, GDriveCallback, DropboxCallback } from '@/pages/auth/OAuthCallback'
import DiscoveryPage          from '@/pages/DiscoveryPage'

// ── Inbox badge hook ──────────────────────────────────────────────────────────

function useInboxBadge(participantId?: string) {
  const { data: recipientId } = useQuery({
    queryKey:  ['recipient-id', participantId],
    queryFn:   async () => {
      if (!participantId) return null
      const { data } = await supabase
        .from('recipients').select('id').eq('participant_id', participantId).single()
      return data?.id ?? null
    },
    enabled:   !!participantId,
    staleTime: 60_000,
  })

  const { data: count = 0 } = useQuery({
    queryKey:      ['inbox-count', recipientId],
    queryFn:       () => fetchInboxCount(recipientId!),
    enabled:       !!recipientId,
    staleTime: 5 * 60_000,
  })

  return count
}

// ── More drawer ───────────────────────────────────────────────────────────────

interface DrawerItem {
  to:    string
  label: string
  desc:  string
  icon:  React.ElementType
  roles?: string[]   // if set, only show for these roles
}

const DRAWER_ITEMS: DrawerItem[] = [
  { to: '/requests',     label: 'Requests',      desc: 'Your deal desk — incoming license requests',  icon: CheckSquare                         },
  { to: '/briefs',       label: 'Buyer briefs',  desc: 'Set your standing briefs for AI matching',    icon: BookOpen                            },
  { to: '/portfolio',    label: 'Portfolio',     desc: 'Your shots, sessions and earnings',           icon: User                                },
  { to: '/dashboard',   label: 'Stats',         desc: 'Upload and routing analytics',                icon: BarChart2                           },
  { to: '/notifications',label: 'Notifications', desc: 'Telegram, WhatsApp and push settings',        icon: Bell                                },
  { to: '/press',        label: 'Press pack',    desc: 'Generate press releases for your event',      icon: Newspaper,  roles: ['press','organiser'] },
  { to: '/organiser',    label: 'Events',        desc: 'Manage your events and access codes',         icon: LayoutDashboard, roles: ['organiser']    },
]

function MoreDrawer({ open, onClose, role }: { open: boolean; onClose: () => void; role?: string }) {
  const navigate   = useNavigate()
  const location   = useLocation()

  const items = DRAWER_ITEMS.filter(item =>
    !item.roles || item.roles.includes(role ?? '')
  )

  function handleNav(to: string) {
    navigate(to)
    onClose()
  }

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div className={cn(
        'fixed bottom-0 inset-x-0 z-40 transition-transform duration-300 ease-out',
        open ? 'translate-y-0' : 'translate-y-full'
      )}>
        <div className="max-w-lg mx-auto px-4 pb-4">
          <div className="bg-room-bg border border-room-border rounded-2xl overflow-hidden shadow-2xl">
            {/* Handle */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-room-border">
              <span className="text-xs font-semibold uppercase tracking-widest text-room-muted">Menu</span>
              <button
                type="button"
                onClick={onClose}
                className="p-1 rounded-lg text-room-muted hover:text-white transition-colors"
              >
                <X size={15} />
              </button>
            </div>

            {/* Items */}
            <div className="p-2">
              {items.map(({ to, label, desc, icon: Icon }) => {
                const isActive = location.pathname === to
                return (
                  <button
                    key={to}
                    type="button"
                    onClick={() => handleNav(to)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left',
                      isActive
                        ? 'bg-room-accent/15 text-white'
                        : 'text-room-muted hover:text-white hover:bg-room-surface'
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                      isActive ? 'bg-room-accent/20' : 'bg-room-surface'
                    )}>
                      <Icon size={15} className={isActive ? 'text-room-accent' : ''} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn('text-sm font-semibold', isActive ? 'text-white' : '')}>{label}</p>
                      <p className="text-xs text-room-muted truncate">{desc}</p>
                    </div>
                    <ChevronRight size={13} className="text-room-muted flex-shrink-0" />
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Bottom nav — 4 tabs only ──────────────────────────────────────────────────

function BottomNav({
  activeCount, errorCount, inboxCount, role: _role, moreOpen, onMoreToggle
}: {
  activeCount: number
  errorCount:  number
  inboxCount:  number
  role?:       string
  moreOpen:    boolean
  onMoreToggle: () => void
}) {
  const location = useLocation()

  // "More" tab is considered active if current path is one of the drawer items
  const drawerPaths = DRAWER_ITEMS.map(i => i.to)
  const moreIsActive = !moreOpen && drawerPaths.includes(location.pathname)

  return (
    <nav className="fixed bottom-0 inset-x-0 z-20">
      <div className="max-w-lg mx-auto px-4 pb-4">
        <div className="bg-black/80 backdrop-blur-xl border border-room-border rounded-2xl p-1.5 flex gap-1">

          {/* Upload */}
          <NavLink to="/upload" className={({ isActive }) => cn(
            'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-0.5 relative',
            isActive ? 'bg-room-accent/80 text-white shadow-lg' : 'text-room-muted hover:text-room-text'
          )}>
            {({ isActive }) => (
              <>
                <Upload size={18} strokeWidth={isActive ? 2.5 : 1.8} />
                Upload
                {(activeCount > 0 || errorCount > 0) && (
                  <span className={cn(
                    'absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center',
                    errorCount > 0 ? 'bg-room-error text-white' : 'bg-room-gold text-black'
                  )}>
                    {errorCount > 0 ? errorCount : activeCount}
                  </span>
                )}
              </>
            )}
          </NavLink>

          {/* Feed */}
          <NavLink to="/feed" className={({ isActive }) => cn(
            'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-0.5',
            isActive ? 'bg-room-accent/80 text-white shadow-lg' : 'text-room-muted hover:text-room-text'
          )}>
            {({ isActive }) => (
              <>
                <Grid size={18} strokeWidth={isActive ? 2.5 : 1.8} />
                Feed
              </>
            )}
          </NavLink>

          {/* Inbox */}
          <NavLink to="/inbox" className={({ isActive }) => cn(
            'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-0.5 relative',
            isActive ? 'bg-room-accent/80 text-white shadow-lg' : 'text-room-muted hover:text-room-text'
          )}>
            {({ isActive }) => (
              <>
                <Inbox size={18} strokeWidth={isActive ? 2.5 : 1.8} />
                Inbox
                {inboxCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center bg-pink-500 text-white">
                    {inboxCount > 9 ? '9+' : inboxCount}
                  </span>
                )}
              </>
            )}
          </NavLink>

          {/* More */}
          <button
            type="button"
            onClick={onMoreToggle}
            className={cn(
              'flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex flex-col items-center gap-0.5',
              moreOpen || moreIsActive
                ? 'bg-room-accent/80 text-white shadow-lg'
                : 'text-room-muted hover:text-room-text'
            )}
          >
            {moreOpen
              ? <X size={18} strokeWidth={2.5} />
              : <Menu size={18} strokeWidth={moreIsActive ? 2.5 : 1.8} />
            }
            More
          </button>

        </div>
      </div>
    </nav>
  )
}

// ── Protected layout ──────────────────────────────────────────────────────────

function ProtectedLayout() {
  const { user, participant, initialized } = useAuthStore()
  const activeCount = useUploadStore(s => s.activeCount())
  const errorCount  = useUploadStore(s => s.errorCount())
  const inboxCount  = useInboxBadge(participant?.id)
  const [moreOpen,  setMoreOpen]  = useState(false)

  const { data: platformConfig } = useQuery({
    queryKey: ['platform-config'],
    queryFn:  fetchPlatformConfig,
    staleTime: 10 * 60_000,
  })

  if (!initialized) {
    return (
      <div className="min-h-screen bg-room-bg flex items-center justify-center">
        <Spinner size={32} />
      </div>
    )
  }

  if (!user && !participant) return <Navigate to="/enter" replace />
  if (!participant)          return <Navigate to="/enter" replace />

  return (
    <div className="min-h-screen bg-room-bg text-white">
      <NetworkBanner />

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-room-accent/5 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-900/8 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-4 pt-8 pb-28">
        <header className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center gap-2 bg-room-surface border border-room-border rounded-full px-3 py-1 text-xs text-room-text mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-room-success animate-pulse-dot" />
            {platformConfig?.evaluation_label ?? 'Ibiza Pilot 2026'} · Live
            {platformConfig?.evaluation_mode && (
              <span className="text-room-accent/70">· Founder access</span>
            )}
          </div>
          <h1 className="text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-br from-purple-300 via-room-accent to-purple-500 bg-clip-text text-transparent">
              The Room
            </span>
          </h1>
          <p className="text-room-muted text-sm">If you're not in The Room, you're missing it.</p>
        </header>

        <Routes>
          <Route path="/upload"        element={<UploadPage />}            />
          <Route path="/feed"          element={<FeedPage />}              />
          <Route path="/inbox"         element={<InboxPage />}             />
          <Route path="/requests"      element={<CreatorRequestsPage />}   />
          <Route path="/dashboard"     element={<DashboardPage />}         />
          <Route path="/organiser"     element={<OrganiserPage />}         />
          <Route path="/briefs"        element={<BuyerBriefPage />}        />
          <Route path="/portfolio"     element={<PortfolioPage />}         />
          <Route path="/press"         element={<PressPage />}             />
          <Route path="/notifications" element={<NotificationPrefsPage />} />
          <Route path="*"              element={<Navigate to="/upload" replace />} />
        </Routes>
      </div>

      <MoreDrawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        role={participant.role}
      />

      <BottomNav
        activeCount={activeCount}
        errorCount={errorCount}
        inboxCount={inboxCount}
        role={participant.role}
        moreOpen={moreOpen}
        onMoreToggle={() => setMoreOpen(v => !v)}
      />
    </div>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"           element={<Navigate to="/enter" replace />} />
        <Route path="/enter"      element={<VenueEntryPage />}  />
        <Route path="/auth"       element={<AuthPage />}        />
        <Route path="/onboarding" element={<OnboardingPage />}  />
        <Route path="/view"       element={<DeepLinkPage />}    />
        <Route path="/venue/*"    element={<VenueApp />}        />
        <Route path="/approve/:token" element={<ApprovalTokenPage />} />
        <Route path="/auth/onedrive/callback" element={<OneDriveCallback />} />
        <Route path="/auth/gdrive/callback"   element={<GDriveCallback />}   />
        <Route path="/auth/dropbox/callback"  element={<DropboxCallback />}  />
        <Route path="/discovery"  element={<DiscoveryPage />}    />
        <Route path="/*"          element={<ProtectedLayout />} />
      </Routes>
    </BrowserRouter>
  )
}
